import Container from "@/components/Container/Container";
import Layout from "@/components/Layout/Layout";
import { H5Css, Heading } from "@/components/Typography";
import { darkColor, mutedBlueColor } from "@/constants/colors";
import { privacyPath } from "@/constants/paths";
import { css } from "@emotion/react";
import { GetStaticProps } from "next";

const Page = () => {
  return (
    <Layout
      title="Terms Of Use"
      metaDescription="Terms Of Use"
      heroContainerCss={css`
        background: url(/images/policies/BG.svg), ${mutedBlueColor};
        background-size: cover;
        background-repeat: no-repeat;
      `}
      hero={
        <Container>
          <Heading
            level={1}
            css={css`
              color: ${darkColor};
              text-align: center;
              margin-top: 40px;
              padding-bottom: 87px;
              ${H5Css}
            `}
          >
            Terms Of Use
          </Heading>
        </Container>
      }
    >
      <Container
        css={css`
          padding: 60px 30px;
        `}
      >
        <p style={{ textAlign: "center" }}>
          Digital Edge Sport, Inc. d/b/a Jersey Watch
          <br />
          Website Terms of Use
        </p>
        <p>Last Revised: April 15, 2022</p>
        <p>
          The website located at{" "}
          <a href="https://www.jerseywatch.com/">
            https://www.jerseywatch.com/
          </a>
          , (the “Website”) is published, owned, and operated by Digital Edge
          Sport, Inc. d/b/a Jersey Watch, its affiliates and related entities
          (“Jersey Watch,” the “Company,” “we,” “us,” and “our”). These Terms of
          Use (the “Terms”) govern your access to and use of the Website and
          applications, services and tools (including, but not limited to,
          building websites, creating online registrations, posting and sending
          announcements and events, adding website administrators and purchasing
          a Jersey Watch plan) (collectively our “Service(s)”).
          <br />
          <br />
          By accessing, browsing, submitting information to and/or using the
          Services, you agree and acknowledge on your own behalf as an
          Administrator, End User or General User, as these terms are defined in
          the Privacy Policy, (referred to throughout the Terms as “you”) that
          you have read, understand and agree to be bound by these Terms and to
          comply with all applicable laws including, without limitation, all
          federal, state and local tax and tariff laws, regulations, and/or
          directives.
        </p>
        <p>
          <strong>
            These Terms include an arbitration clause. Please read the Terms
            carefully before you start to use the Services.
          </strong>
          <br />
          <br />
          <strong>
            <u>
              If you do not agree to these Terms, please do not use the Website
            </u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          <strong>
            1. <u>Purpose of the Website</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          The Website is provided for informational purposes, to provide our
          Services and for the purposes of enabling communication between you
          and the Company. The information provided is intended to be general in
          nature and does not necessarily address all the terms, exclusions, and
          conditions applicable to our Services.
          <br />
          <br />
          We do not warrant the accuracy, completeness, or usefulness of this
          information at any particular time. Any reliance you place on such
          information is strictly at your own risk. The Company disclaims all
          liability and responsibility arising from any reliance placed on such
          content by you or any other visitor to our Website, or by anyone who
          may be informed of any of its contents. Any information you provide or
          that is collected by the Company through the Website shall be handled
          in accordance with the Website&apos;s{" "}
          <a href={privacyPath}>Privacy Policy</a>, which is hereby incorporated
          by reference. All capitalized terms not defined in these Terms shall
          have the same meaning, as applicable, as set forth in the Privacy
          Policy.
        </p>
        <p>
          <strong>
            2. <u>Use of the Website</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          The Company grants you a non-exclusive right to access and use the
          Website and the data, material, content, or information herein
          (collectively, the “Content”) solely for your individual use. Your
          right to access and use the Website shall be limited to non-commercial
          purposes unless you are otherwise expressly authorized by the Company
          to use the Website for commercial purposes. You agree to use the
          Website only for lawful purposes, comply with all rules governing any
          transactions on and through the Website and comply with applicable
          laws.
        </p>
        <p>
          <strong>
            3. <u>User Account Responsibility</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          To access the Website, Services or some of the resources we offer, you
          may be asked to provide certain registration details or other
          information. It is a condition of your use of the Services that all
          the information you provide on the Services is correct, current, and
          complete. You agree that any information you provide to register with
          the Services or otherwise, including but not limited to the use of any
          interactive features on the Services, is governed by our{" "}
          <a href={privacyPath}>Privacy Policy</a>, and you consent to all the
          actions we take with respect to your information consistent with our
          Privacy Policy.
        </p>
        <p>
          If you are given or create a password to access the Services, you are
          responsible for maintaining the confidentiality of your account and
          your password. You also acknowledge that your account is individual to
          you and agree not to provide any other person with access to the
          Services or portions of it using your user name, password, or other
          security information. You are responsible for all activities that
          occur under your account and you agree to notify the Company
          immediately of any unauthorized use of your account. You also agree to
          ensure that you exit from your account at the end of each session. You
          should use particular caution when accessing your account from a
          public or shared computer so that others are not able to view or
          record your password or other personal information. We are not
          responsible for any loss that you may incur as a result of any
          unauthorized person using your account or your password.
        </p>
        <p>
          We have the right to disable any user name, password or other
          identifier, whether chosen by you or provided by us, at any time in
          our sole discretion for any or no reason, including if, in our
          opinion, you have violated any provision of these Terms.
        </p>
        <p>
          <strong>
            4. <u>Prohibited Uses</u>
          </strong>
          <strong>.</strong> You agree that you will not:
        </p>
        <ul>
          <li>
            Use the Website or the Services in any manner that could damage,
            disable, overburden, or impair the Website or interfere with any
            other party&apos;s use and enjoyment of them;
          </li>
          <li>
            Attempt to gain unauthorized access to any Website account, computer
            systems or networks associated with the Company or the Website;
          </li>
          <li>
            Obtain or attempt to obtain any materials or information through the
            Website by any means not intentionally made available or provided by
            the Company;
          </li>
          <li>
            Use any robot, spider, or other automatic device, process or means
            to access the Website for any purpose, including monitoring or
            copying any of the material on the Website;
          </li>
          <li>
            Introduce any viruses, Trojan horses, worms, logic bombs, or other
            material which is malicious or technologically harmful;
          </li>
          <li>
            Attack the Website via a denial-of-service attack or a distributed
            denial-of-service attack; or
          </li>
          <li>
            Impersonate or attempt to impersonate the Company, a Company
            employee, another user, or any other person or entity (including,
            without limitation, by using email addresses associated with any of
            the foregoing).
          </li>
        </ul>
        <p>
          <strong>
            5. <u>Linking to the Website</u>
          </strong>
          <strong>.</strong>
          <strong> </strong>
        </p>
        <p>
          You may link to our Website or Administrator Sites, provided you do so
          in a way that is fair and legal and does not damage our reputation or
          take advantage of it, but you must not establish a link in such a way
          as to suggest any form of association, approval, or endorsement on our
          part.
        </p>
        <p>
          <strong>
            6. <u>Third-party Websites</u>
          </strong>
          .
        </p>
        <p>
          The Website or Administrator Sites may contain links to websites
          controlled or operated by persons and companies other than the Company
          (“Linked Sites”). Linked Sites are not under the control of the
          Company, and the Company is not responsible for the contents of any
          Linked Site, including without limitation any link contained on a
          Linked Site, or any changes or updates to a Linked Site. The Company
          is not responsible if the Linked Site is not working correctly or for
          any viruses, malware, or other harms resulting from your use of a
          Linked Site. The Company is providing these links to you only as a
          convenience, and the inclusion of any link does not imply endorsement
          by the Company of the site or any association with its operators. You
          are responsible for viewing and abiding by the privacy policies and
          terms of use posted on the Linked Sites. You are solely responsible
          for any dealings with third parties who support the Company or are
          identified in the Website, including any delivery of and payment for
          goods and services.
        </p>
        <p>
          <strong>
            7. <u>Intellectual Property Notices</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          The Website, Content, and our Services are protected by copyrights,
          trademarks, or are subject to other proprietary rights. Accordingly,
          you are not permitted to use the Website or Content in any manner,
          except as expressly permitted by the Company in these Terms. The
          Website, Content, or our Services may not be copied, reproduced,
          modified, published, uploaded, posted, transmitted, performed, or
          distributed in any way, and you agree not to modify, rent, lease,
          loan, sell, distribute, transmit, broadcast, or create derivatives
          without the express written consent of the Company or applicable
          owner.
        </p>
        <p>
          Your computer may temporarily store copies of Content incidental to
          your accessing and viewing those Content. You may store files that are
          automatically cached by your web browser for display enhancement
          purposes. You may print or download one copy of a reasonable number of
          pages of the Website for your own personal, non-commercial use related
          to purchasing a plan on this Website, if applicable, and not for
          further reproduction, publication, or distribution.
        </p>
        <p>
          <u>Copyright</u>. You should assume that everything you see or read on
          the Company&apos;s Website is copyrighted unless otherwise noted and
          may not be used without the written permission of the Company. The
          Company neither warrants nor represents that your use of materials
          displayed on the Company&apos;s Website will not infringe the rights
          of third parties. Content, images, photographs, or illustrations
          displayed on the Company&apos;s Website is either property of, or used
          with permission by, the Company. The use of these materials by you, or
          anyone else authorized by you, is prohibited unless specifically
          permitted by these Terms or specific permission provided elsewhere on
          the Company&apos;s Website. Any unauthorized use of any content,
          images, photographs, or illustrations may violate copyright laws,
          trademark laws, the laws of privacy and publicity, and communications
          regulations and statutes.
        </p>
        <p>
          <u>Trademark</u>. Jersey Watch is a registered trademark owned by the
          Company. Nothing contained on the Company&apos;s Website should be
          construed as granting or conveying, by implication, or otherwise, any
          license or right to use any trademark displayed on the Company&apos;s
          Website without the written permission of the Company or such third
          party that may own a trademark displayed on the Company&apos;s
          Website. Your misuse of the Company&apos;s trademark(s) displayed on
          the Company&apos;s Website, or any other Content on the Company&apos;s
          Website, except as provided herein, is strictly prohibited.
        </p>
        <p>
          <u>User Contributions</u>. The Services allow you to post, submit,
          publish, display, or transmit to other users or other persons
          (hereinafter, &quot;post&quot;) content or materials (collectively,
          &quot;User Contributions&quot;) to Administrator Sites. All User
          Contributions must comply with the Content Standards set out in these
          Terms.
        </p>
        <p>
          Any User Contribution you post through the Services will be considered
          non-confidential and non-proprietary. By providing any User
          Contribution on the Services, you grant us and our affiliates and
          service providers, and each of their and our respective licensees,
          successors and assigns an irrevocable, royalty-free, fully paid up,
          worldwide, non-exclusive license to use, reproduce, modify, perform,
          display, distribute, and otherwise disclose to third parties any such
          material for any purpose.
        </p>
        <p>You represent and warrant that:</p>
        <ul>
          <li>
            You own or control all rights in and to the User Contributions and
            have the right to grant the license granted above to us and our
            affiliates and service providers, and each of their and our
            respective licensees, successors, and assigns.
          </li>
          <li>
            All of your User Contributions do and will comply with these Terms.
          </li>
        </ul>
        <p>
          You understand and acknowledge that you are solely responsible for any
          User Contributions you submit or contribute, and you, not the Company,
          have full responsibility for such content, including its legality,
          reliability, accuracy, and appropriateness. The Services may also
          provide you with features like photo thumbnails, previews, easy
          sorting, editing, sharing, creating templates, and searching. These
          and other features may require our Company systems to access, store,
          and scan User Contributions. You may also be able to share User
          Contributions with others, so please think carefully about what you
          share.
        </p>
        <p>
          We are not responsible, or liable to any third party, for the content
          or accuracy of any User.
        </p>
        <p>
          <u>Compliance</u>.
        </p>
        <p>
          You also acknowledge and agree that you are solely responsible for
          complying with the applicable restrictions on use of all Content,
          copyrighted materials, and trademarks that you see, hear, and use on
          the Website. You understand that any unauthorized use of such
          intellectual property would result in irreparable injury for which
          money damages would be inadequate. You further acknowledge that, in
          the event of any such unauthorized use, the Company or the applicable
          intellectual property owner will have the right, in addition to other
          remedies available at law and in equity, to immediate injunctive
          relief to prevent any such unauthorized use.
          <br />
          <br />
          Questions regarding the use of any intellectual property provided on
          the Website should be directed to{" "}
          <a href="mailto:contact@jerseywatch.com">contact@jerseywatch.com</a>.
        </p>
        <p>
          <strong>
            8. <u>Reliance on Information Posted</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          The information presented on or through the Website or our Services is
          made available solely for general information purposes. We do not
          warrant the accuracy, completeness, or usefulness of this information.
          Any reliance you place on such information is strictly at your own
          risk. We disclaim all liability and responsibility arising from any
          reliance placed on such materials by you or any other visitor to the
          Services, or by anyone who may be informed of any of its contents.
        </p>
        <p>
          The Website, Services, and Administrator Sites may include content
          provided by third parties, including materials provided by other
          users. All statements and/or opinions expressed in these materials,
          and all articles and responses to questions and other content, other
          than the content provided by the Company, are solely the opinions and
          the responsibility of the person or entity providing those materials.
          These materials do not necessarily reflect the opinion of the Company.
          We are not responsible, or liable to you or any third party, for the
          content or accuracy of any materials provided by any third parties.
        </p>
        <p>
          <strong>
            9. <u>Company Rights and Enforcement</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>We reserve the right to: </p>
        <ul>
          <li>
            Withdraw or amend the Website, Services and any information or
            material we provide on the Services, in our sole discretion without
            notice. We will not be liable if for any reason all or any part of
            the Website or Services is unavailable at any time or for any
            period. From time to time, we may restrict access to all or some
            parts of the Website or Services, to users.
          </li>
          <li>
            Remove or refuse to post any User Contributions (defined below) for
            any or no reason in our sole discretion.
          </li>
          <li>
            Take any action with respect to any User Contribution that we deem
            necessary or appropriate in our sole discretion, including if we
            believe that such User Contribution violates these Terms, including
            the Content Standards (described below), infringes any intellectual
            property right or other right of any person or entity, threatens the
            personal safety of users of the Services or the public or could
            create liability for the Company.
          </li>
          <li>
            Disclose your identity or other information about you to any third
            party who claims that material posted by you violates their rights,
            including their intellectual property rights or their right to
            privacy.
          </li>
          <li>
            Take appropriate legal action, including without limitation,
            referral to law enforcement, for any illegal or unauthorized use of
            the Website or our Services.
          </li>
          <li>
            Terminate or suspend your access to all or part of the Website or
            our Services for any or no reason, including without limitation, any
            violation of these Terms.
          </li>
        </ul>
        <p>
          Without limiting the foregoing, we have the right to fully cooperate
          with any law enforcement authorities or court order requesting or
          directing us to disclose the identity or other information of anyone
          posting any materials on or through the Services.
          <strong>
            YOU WAIVE AND HOLD HARMLESS THE COMPANY AND ITS AFFILIATES,
            LICENSEES AND SERVICE PROVIDERS FROM ANY CLAIMS RESULTING FROM ANY
            ACTION TAKEN BY THE COMPANY/ANY OF THE FOREGOING PARTIES DURING OR
            AS A RESULT OF ITS INVESTIGATIONS AND FROM ANY ACTIONS TAKEN AS A
            CONSEQUENCE OF INVESTIGATIONS BY EITHER THE COMPANY/SUCH PARTIES OR
            LAW ENFORCEMENT AUTHORITIES.
          </strong>
        </p>
        <p>
          However, we do not undertake to review all material before it is
          posted on the Website, and cannot ensure prompt removal of
          objectionable material after it has been posted. Accordingly, we
          assume no liability for any action or inaction regarding
          transmissions, communications, or content provided by any user or
          third party. We have no liability or responsibility to anyone for
          performance or nonperformance of the activities described in this
          section.
        </p>
        <p>
          <strong>
            10. <u>Content Standards</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          These content standards apply to any and all User Contributions posted
          on our Website or any Administrator Site. User Contributions must, in
          their entirety, comply with all applicable federal, state, local, and
          international laws and regulations. Without limiting the foregoing,
          User Contributions must not:
        </p>
        <ul>
          <li>
            Contain any material which is defamatory, obscene, indecent,
            abusive, offensive, harassing, violent, hateful, inflammatory, or
            otherwise objectionable.
          </li>
          <li>
            Promote sexually explicit or pornographic material, violence, or
            discrimination based on race, sex, religion, nationality,
            disability, sexual orientation, or age.
          </li>
          <li>
            Infringe any patent, trademark, trade secret, copyright or other
            intellectual property or other rights of any other person.
          </li>
          <li>
            Violate the legal rights (including the rights of publicity and
            privacy) of others or contain any material that could give rise to
            any civil or criminal liability under applicable laws or regulations
            or that otherwise may be in conflict with these Terms or our Privacy
            Policy.
          </li>
          <li>Be likely to deceive any person.</li>
          <li>
            Promote any illegal activity, or advocate, promote or assist any
            unlawful act.
          </li>
          <li>
            Cause annoyance, inconvenience, or needless anxiety or be likely to
            upset, embarrass, alarm, or annoy any other person.
          </li>
          <li>
            Impersonate any person, or misrepresent your identity or affiliation
            with any person or organization.
          </li>
          <li>
            Involve commercial activities or sales, such as contests,
            sweepstakes and other sales promotions, barter or advertising.
          </li>
          <li>
            Give the impression that they emanate from or are endorsed by us or
            any other person or entity, if this is not the case.
          </li>
        </ul>
        <p>
          <strong>
            11. <u>Uploading Personal Records</u>
          </strong>
          <strong>.</strong>
          <strong> </strong>
        </p>
        <p>
          In the course of your use of the Services, you may choose or be
          required by a third party to upload Personal Records that contain
          Personal Information to our Website. Such Personal Records may
          include, but are not limited to, birth certificates, vaccination
          records, and medical examination records for individual players or
          team members. When providing your Personal Records, please provide
          only relevant information and do not provide unnecessary sensitive
          information, such as Social Security cards, photos or scans of credit
          card or other documents containing sensitive personal data, unless
          required for our Services. Such records will only be retained for as
          long as necessary to provide our Services and in accordance with
          applicable law and our documented policies. All records will be
          securely maintained and encrypted in accordance with Company policy
          and procedures.
        </p>
        <p>
          Consent. By uploading Personal Records to the Jersey Watch Website,
          you acknowledge and freely consent to let to let the Company use and
          disclose the Personal Record and any Personal Information contained in
          the Personal Record, as applicable.
        </p>
        <p>
          Withdraw Consent. At any time, you may withdraw my consent to the
          processing and uploading of any Personal Record by notifying the
          Company via email at{" "}
          <a href="mailto:contact@jerseywatch.com">contact@jerseywatch.com</a>{" "}
          using the same email address associated with my Website account (if
          applicable), with the words “WITHDRAW CONSENT” in the email subject
          line.
        </p>
        <p>
          <strong>
            12. <u>United States and Canada Only</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          The Company is based in the state of Ohio in the United States. The
          Company provides this Website for use only by persons located in the
          United States and Canada. We make no claims that the Website,
          Services, or any of its contents are accessible or appropriate outside
          of the United States and Canada. Access to the Website or our Services
          may not be legal by certain persons or in certain countries. User
          accounts created outside the United States and Canada will be removed
          from the Website.
        </p>
        <p>
          <strong>
            13. <u>Fees and Payment Terms</u>
          </strong>
          <strong>. </strong>
        </p>
        <p>
          By selecting and using any Services that we offer for a fee on a
          subscription basis, you accept the cost of the Services that you have
          elected to pay, and you authorize Jersey Watch and/or our trusted
          third-party partners to process your payment from the credit or debit
          card or other payment source you have identified through your Jersey
          Watch account (“Payment Method”). If you purchase any Services that we
          offer for a fee, you agree to pay Jersey Watch, directly or via its
          designated third-party payment processor, all disclosed and applicable
          fees as they become due plus all applicable related taxes. You may
          cancel your subscription from time to time as provided on the Website.
          We may refuse to renew any subscription.
        </p>
        <p>
          Unless otherwise agreed to in writing, if you select a Service subject
          to a recurring monthly subscription fee for use of the Services (the
          “Subscription Fee(s)”), notwithstanding any then-applicable payment
          arrangement, you agree to pay the Subscription Fee and any
          volume-based or consumption-based charges applicable to the Services
          that you have selected. Such Subscription Fees may vary depending upon
          the subscription selected by you. Your billing will be provided by
          Jersey Watch or our trusted third-party partners. The Subscription Fee
          will be billed on an ongoing regular monthly basis unless you cancel
          your plan or pay a one-time annual fee. Billing may also be pro-rated
          based upon the timing of cancellation within the billing cycle. Please
          note that billing cycles may vary. Note that the timing of your
          billing may change (i.e., in the event of a problem with your Payment
          Method, such as an expired credit card). You should confirm the
          applicable billing policies prior to establishing its subscription.
          Current Subscription Fees associated with the Services can be found
          here:{" "}
          <a href="https://www.jerseywatch.com/pricing/">
            https://www.jerseywatch.com/pricing/
          </a>
          . Jersey Watch may change the Subscription Fee and adjust pricing for
          the Services or any components thereof in any manner and at any time
          as we may determine in our sole and absolute discretion. We will
          notify you in advance of any change in Subscription Fees by posting
          such changes to the Subscription Fee at the link above. You accept the
          new fees by continuing to use the Services after the fee change takes
          effect. If you do not agree with the applicable price change, you have
          the right to reject the change by terminating your Jersey Watch
          account through the Website in accordance with these Terms.
        </p>
        <p>
          You may update your Payment Method with Jersey Watch at any time by
          accessing your Jersey Watch account. If at any time Jersey Watch is
          unable to charge your Payment Method for the Subscription Fee due to
          insufficient funds, expired or invalid Jersey Watch account details,
          or otherwise, you remain responsible for the cost of such Subscription
          Fee. A change in your Payment Method may result in changes to your
          prior billing cycle. It is your responsibility to update and maintain
          the Payment Method associated with your Subscription Fee. In the event
          your Payment Method is unable to cover the Subscription Fee, Jersey
          Watch reserves the right to suspend your access to the Services until
          such time as the payment method has been updated and the Subscription
          Fee is duly paid.
        </p>
        <p>
          Jersey Watch reserves the right to retry or permit its third-party
          partners to retry billing your Payment Method after failed attempts
          (e.g., if Payment Method is rejected). We also reserve the right to
          pursue or permit our trusted third-party partners to pursue any
          amounts you fail to pay in connection with your subscription. If
          Jersey Watch is unable to collect your payment using one of the
          foregoing methods, Jersey Watch reserves the right to freeze or cancel
          your Jersey Watch account in its sole discretion. You will remain
          liable for all such amounts and all costs incurred in connection with
          the collection of any unpaid amount, including, without limitation,
          bank overdraft fees, collection agency fees, reasonable
          attorneys&apos; fees, and litigation and/or arbitration costs.
        </p>
        <p>
          All information that you provide to register with Jersey Watch,
          including, without limitation, your Payment Method, is subject to our
          Privacy Policy and these Terms. We may use a third-party payment
          service to charge your Payment Method. By submitting your information
          relating to your Payment Method, you grant Jersey Watch the right to
          store and process your information with the third-party payment
          service, which may change from to time. You agree that Jersey Watch
          will not be responsible for any failures of the third party to
          adequately protect such information. You acknowledge that we may
          charge (or use a third-party payment service to charge) its Payment
          Method for any applicable fees associated with your Jersey Watch
          account or other amounts owed to Jersey Watch in connection with your
          use or misuse of the Services or Website. Without limiting the
          foregoing, if any payment or transfer you make from your Jersey Watch
          account is returned unpaid, you authorize Jersey Watch (or a
          third-party payment service) to make a one-time electronic fund
          transfer from its Payment Method to collect an amount equal to the fee
          charged for such return. Additional information about payments
          associated with the Services can be found here:{" "}
          <a href="https://www.jerseywatch.com/faq/">
            https://www.jerseywatch.com/faq/
          </a>
          .
        </p>
        <p>
          YOU ARE RESPONSIBLE FOR THE TIMELY PAYMENT OF ANY FEES AND FOR
          PROVIDING JERSEY WATCH WITH A VALID PAYMENT METHOD FOR PAYMENT OF ALL
          FEES THROUGH ONE OF THE METHODS THAT WE LIST ON OUR WEBSITE FROM TIME
          TO TIME.
        </p>
        <p>
          <strong>
            14. <u>Arbitration</u>
          </strong>
          .
          <br />
          <br />
          At its sole discretion, the Company may require you to submit any
          disputes arising from use of the Services, or breach of these Terms,
          including disputes arising from or concerning their interpretation,
          violation, invalidity, non-performance, or termination, to final and
          binding arbitration under the Rules of Arbitration of the American
          Arbitration Association applying Ohio law. By using the Services, and
          should the Company choose such arbitration to resolve any disputes,
          you hereby consent to submission of any such dispute to be final and
          binding arbitration.
        </p>
        <p>
          <strong>
            15. <u>Disclaimer</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          ALL INFORMATION OR SERVICES PROVIDED BY THE COMPANY TO YOU VIA THE
          WEBSITE, INCLUDING, WITHOUT LIMITATION, ALL CONTENT, ARE PROVIDED “AS
          IS” AND “WHERE IS” AND WITHOUT ANY WARRANTIES OF ANY KIND. THE COMPANY
          AND ITS THIRD-PARTY LICENSORS EXPRESSLY DISCLAIM ALL WARRANTIES,
          WHETHER EXPRESS, IMPLIED OR STATUTORY, INCLUDING, WITHOUT LIMITATION,
          THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
          TITLE AND NON-INFRINGEMENT. NOTWITHSTANDING ANY PROVISION CONTAINED
          HEREIN TO THE CONTRARY, THE COMPANY AND ITS THIRD-PARTY LICENSORS MAKE
          NO REPRESENTATION, WARRANTY OR COVENANT CONCERNING THE ACCURACY,
          QUALITY, SUITABILITY, COMPLETENESS, SEQUENCE, TIMELINESS, SECURITY OR
          AVAILABILITY OF THE WEBSITE OR ANY CONTENT POSTED ON OR OTHERWISE
          ACCESSIBLE VIA THE WEBSITE. YOU SPECIFICALLY ACKNOWLEDGE THAT THE
          COMPANY AND ITS THIRD-PARTY LICENSORS ARE NOT LIABLE FOR THE
          DEFAMATORY, OBSCENE OR UNLAWFUL CONDUCT OF OTHER THIRD PARTIES OR
          USERS OF THE WEBSITE AND THAT THE RISK OF INJURY FROM THE FOREGOING
          RESTS ENTIRELY WITH YOU. NEITHER THE COMPANY NOR ANY OF ITS
          THIRD-PARTY LICENSORS REPRESENT, WARRANT OR COVENANT THAT THE WEBSITE
          WILL BE SECURE, UNINTERRUPTED OR ERROR-FREE. THE COMPANY FURTHER MAKES
          NO WARRANTY THAT THE WEBSITE WILL BE FREE OF VIRUSES, WORMS OR TROJAN
          HORSES OR THAT IT WILL FUNCTION OR OPERATE IN CONJUNCTION WITH ANY
          OTHER PRODUCT OR SOFTWARE. YOU EXPRESSLY AGREE THAT USE OF THE WEBSITE
          IS AT YOUR SOLE RISK AND THAT THE COMPANY, ITS AFFILIATES AND THEIR
          THIRD-PARTY LICENSORS SHALL NOT BE RESPONSIBLE FOR ANY TERMINATION,
          INTERRUPTION OF SERVICES, DELAYS, ERRORS, FAILURES OF PERFORMANCE,
          DEFECTS, LINE FAILURES, OR OMISSIONS ASSOCIATED WITH THE WEBSITE OR
          YOUR USE THEREOF. YOUR SOLE REMEDY AGAINST THE COMPANY FOR
          DISSATISFACTION WITH THE WEBSITE OR THE CONTENT IS TO CEASE YOUR USE
          OF THE WEBSITE AND/OR THE CONTENT. SOME JURISDICTIONS DO NOT PERMIT
          THE EXCLUSION OR LIMITATION OF IMPLIED WARRANTIES, SO THE ABOVE
          EXCLUSION MAY NOT APPLY TO YOU. YOU MAY HAVE OTHER RIGHTS, WHICH VARY
          BY JURISDICTION. WHEN THE IMPLIED WARRANTIES ARE NOT ALLOWED TO BE
          EXCLUDED IN THEIR ENTIRETY, YOU AGREE THAT THEY WILL BE LIMITED TO THE
          GREATEST EXTENT AND SHORTEST DURATION PERMITTED BY LAW.
        </p>
        <p>
          <strong>
            16. <u>Limitation of Liability</u>
          </strong>
          <strong>. </strong>
        </p>
        <p>
          UNDER NO CIRCUMSTANCES SHALL THE COMPANY OR ANY OF ITS THIRD-PARTY
          LICENSORS BE LIABLE TO YOU OR TO ANY THIRD PARTY FOR ANY DIRECT,
          INDIRECT, INCIDENTAL, CONSEQUENTIAL, PUNITIVE, SPECIAL OR EXEMPLARY
          DAMAGES (INCLUDING, WITHOUT LIMITATION, LOSS OF PROFITS, LOSS OF USE,
          LOSS OF DATA, LOSS OF INFORMATION OR PROGRAMS ON YOUR DATA HANDLING
          SYSTEM, TRANSACTION LOSSES, OPPORTUNITY COSTS, INTERRUPTION OF
          BUSINESS OR COSTS OF PROCURING SUBSTITUTE GOODS) RESULTING FROM,
          ARISING OUT OF OR IN ANY WAY RELATING TO THE WEBSITE, OR THE DATA,
          CONTENT OR INFORMATION ACCESSED VIA THE WEBSITE OR ANY HYPERLINKED
          WEBSITE, OR ANY DISRUPTION OR DELAY IN THE PERFORMANCE OF THE WEBSITE,
          REGARDLESS OF THE FORM OF THE CLAIM OR ACTION, WHETHER BASED ON
          CONTRACT, TORT, STRICT LIABILITY, STATUTE OR OTHERWISE, AND REGARDLESS
          OF WHETHER OR NOT SUCH DAMAGES WERE FORESEEN, UNFORESEEN OR
          FORESEEABLE, EVEN IF THE COMPANY OR ITS THIRD-PARTY LICENSORS HAVE
          BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. BY ACCESSING THE
          WEBSITE SUBJECT TO THESE TERMS OF USE, YOU UNDERSTAND THAT YOU ARE
          WAIVING RIGHTS WITH RESPECT TO CLAIMS THAT ARE AT THIS TIME UNKNOWN OR
          UNSUSPECTED, AND IN ACCORDANCE WITH SUCH WAIVER, YOU ACKNOWLEDGE THAT
          YOU HAVE READ AND UNDERSTOOD, AND HEREBY EXPRESSLY WAIVE, THE BENEFITS
          OF SECTION 1542 OF THE CIVIL CODE OF CALIFORNIA AND ANY SIMILAR LAW OF
          ANY STATE, COUNTRY OR TERRITORY, WHICH PROVIDES AS FOLLOWS: “A GENERAL
          RELEASE DOES NOT EXTEND TO CLAIMS WHICH THE CREDITOR DOES NOT KNOW OR
          SUSPECT TO EXIST IN HIS OR HER FAVOR AT THE TIME OF EXECUTING THE
          RELEASE, WHICH IF KNOWN BY HIM OR HER MUST MATERIALLY AFFECT HIS OR
          HER SETTLEMENT WITH THE DEBTOR.”
        </p>
        <p>
          <strong>
            17. <u>Indemnity</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          You agree to defend, indemnify, and hold harmless the Company and its
          affiliates, licensors and service providers from and against any
          claims, liabilities, damages, judgments, awards, losses, costs,
          expenses or fees (including reasonable attorneys&apos; fees) arising
          out of or relating to your violation of these Terms or your use of the
          Website, including, but not limited to, any use of the Website
          Content, Services&apos; content, or products other than as expressly
          authorized in these Terms or your use of any information obtained from
          the Website.
        </p>
        <p>
          <strong>
            18. <u>Termination and Restriction of Access</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          In its sole discretion, the Company may terminate or suspend your
          access to the Website for breach of these Terms. The Company shall not
          be liable for any losses or damages arising from any such termination
          of service.
        </p>
        <p>
          <strong>
            19. <u>Limitation on Time to File Claims</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          Any cause of action or claim you may have arising out of or relating
          to these Terms, the Website or Services must be commenced within one
          year after the cause of action accrues, otherwise, such cause of
          action or claim is permanently barred.
        </p>
        <p>
          <strong>
            20. <u>Governing Law &amp; Jurisdiction</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          These Terms are governed by the laws of the State of Ohio, U.S.A.
          Should the Company not choose arbitration to resolve any dispute under
          these Terms, you hereby irrevocably consent to the exclusive
          jurisdiction and venue of the courts in Cincinnati, Ohio U.S.A. in all
          disputes arising out of or relating to the use of the Website.
        </p>
        <p>
          You waive any and all objections to the exercise of jurisdiction over
          you by such courts and to venue in such courts.
        </p>
        <p>
          <strong>
            21. <u>Changes to these Terms of Use</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          We may revise and update these Terms from time to time, in our sole
          discretion, in order to reflect changes in any offered services,
          changes in the law, or for other reasons as deemed necessary by the
          Company. The effective date of any Terms will be reflected in the
          “Last Revised” entry at the top of these Terms. All changes are
          effective immediately when we post them, and apply to all access to
          and use of the Services thereafter. However, any changes to the
          dispute resolution provisions set forth in the Governing Law &amp;
          Jurisdiction section of these Terms will not apply to any disputes for
          which the parties have actual notice prior to the date the change is
          posted on the Services. Your continued use of the Website after any
          such change is communicated shall constitute your consent to such
          change(s). You are expected to check this page frequently so you are
          aware of any changes, as they are binding on you.
        </p>
        <p>
          <strong>
            22. <u>General</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          You agree that no joint venture, partnership, employment, or agency
          relationship exists between you and the Company as a result of these
          Terms or use of the Website. You may not assign these Terms without
          the prior written consent of the Company in all instances. The Company
          may assign these Terms, in whole or in part, at any time. The
          Company&apos;s performance of this agreement is subject to existing
          laws and legal process, and nothing contained in these Terms is in
          derogation of the Company&apos;s right to comply with governmental,
          court, and law enforcement requests or requirements relating to your
          use of the Website or information provided to or gathered by the
          Company with respect to such use.
        </p>
        <p>
          No waiver by the Company of any term or condition set forth in these
          Terms shall be deemed a further or continuing waiver of such term or
          condition or a waiver of any other term or condition, and any failure
          of the Company to assert a right or provision under these Terms shall
          not constitute a waiver of such right or provision.
          <br />
          <br />
          If any part of these Terms are determined to be invalid or
          unenforceable pursuant to applicable law including, but not limited
          to, the warranty disclaimers and liability limitations set forth
          above, then the invalid or unenforceable provision will be deemed
          superseded by a valid, enforceable provision that most closely matches
          the intent of the original provision, and the remainder of these Terms
          shall continue in effect.
        </p>
        <p>
          <strong>
            23. <u>Entire Agreement</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          These Terms, including the Privacy Policy and all other documents
          expressly incorporated herein by reference, constitute the entire
          agreement between you and the Company with respect to the Services,
          and supersede all prior or contemporaneous communications and
          proposals, whether electronic, oral, or written, between you and the
          Company. A printed version of these Terms and of any notices given in
          electronic form shall be admissible in judicial or administrative
          proceedings based upon or relating to this agreement to the same
          extent and subject to the same conditions as other business documents
          and records originally generated and maintained in printed form.
        </p>
        <p>
          <strong>
            24. <u>Company Contact Information</u>
          </strong>
          <strong>.</strong>
        </p>
        <p>
          Questions can be directed to the Company at:{" "}
          <a href="mailto:contact@jerseywatch.com">contact@jerseywatch.com</a>.
        </p>
        <style jsx>{`
          p {
            margin-top: 12px;
          }
        `}</style>
      </Container>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return { props: {} };
};

export default Page;
